import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';

/**
 * Customer ID verification. Uploads to the private id-documents bucket
 * under their own user_id folder (storage RLS enforces this), then tells
 * the API to flip their profile to 'pending'.
 *
 * Accepted: JPEG, PNG, HEIC, PDF, up to 10MB. Admin then reviews.
 */
export function VerifyId() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!user) {
    return (
      <section className="screen">
        <div className="scroll narrow-scroll">
          <div className="ck-head">
            <h1>ID VERIFICATION</h1>
            <div className="p">You need an account to upload your ID.</div>
          </div>
          <div className="footer-cta pad-bottom">
            <Link to="/sign-in" className="cta-full">
              SIGN IN
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const status = profile?.id_status ?? 'not_submitted';

  async function handleFile(file: File) {
    if (!user) return;
    setError(null);
    if (file.size > 10 * 1024 * 1024) {
      setError('That file is over 10MB. Try a smaller image.');
      return;
    }
    if (!/^(image\/|application\/pdf$)/.test(file.type)) {
      setError('Please upload a JPEG, PNG, HEIC or PDF.');
      return;
    }

    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
      const path = `${user.id}/id-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('id-documents')
        .upload(path, file, { contentType: file.type, upsert: true });
      if (upErr) throw new Error(upErr.message);

      await apiFetch('/api/account/id/submit', {
        method: 'POST',
        body: JSON.stringify({ storage_path: path })
      });
      if (refreshProfile) await refreshProfile();
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="screen">
      <div className="scroll narrow-scroll">
        <div className="ck-head">
          <h1>VERIFY YOUR ID</h1>
          <div className="p">
            Upload a clear photo of your UK photo ID — Driving licence, Passport, or
            government ID card. We need it to confirm your identity before your first
            order ships. Stored privately and only reviewed by our team.
          </div>
        </div>

        {/* Status panel */}
        <div
          style={{
            margin: '0 16px 14px',
            padding: '14px 18px',
            background:
              status === 'approved'
                ? 'var(--good-bg)'
                : status === 'rejected'
                  ? '#f5dad6'
                  : status === 'pending'
                    ? 'var(--warn-bg)'
                    : 'var(--card)',
            color:
              status === 'approved'
                ? 'var(--good)'
                : status === 'rejected'
                  ? 'var(--danger)'
                  : status === 'pending'
                    ? 'var(--warn)'
                    : 'var(--ink-soft)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius)',
            fontSize: 13,
            fontWeight: 600
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.16em',
              fontWeight: 700,
              marginBottom: 4
            }}
          >
            STATUS
          </div>
          {status === 'approved' && '✓ Verified — thanks. You can rent without restriction.'}
          {status === 'pending' && '⏳ Awaiting review. We usually get back within 24 hours.'}
          {status === 'rejected' && (
            <>
              ⚠ Rejected — please try again.
              {profile?.id_rejection_reason && (
                <div
                  style={{
                    marginTop: 6,
                    fontWeight: 400,
                    fontSize: 12,
                    lineHeight: 1.4
                  }}
                >
                  Reason: {profile.id_rejection_reason}
                </div>
              )}
            </>
          )}
          {status === 'not_submitted' && 'Not submitted yet.'}
        </div>

        {error && (
          <div className="form-error" style={{ margin: '0 16px 10px' }}>
            {error}
          </div>
        )}
        {success && (
          <div
            style={{
              margin: '0 16px 10px',
              padding: '12px 14px',
              background: 'var(--good-bg)',
              color: 'var(--good)',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600
            }}
          >
            Uploaded — we&rsquo;ll review and email you within 24 hours.
          </div>
        )}

        {/* Upload tile */}
        {(status === 'not_submitted' || status === 'rejected') && (
          <div style={{ padding: '6px 16px 0' }}>
            <div
              onClick={() => !uploading && inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f) void handleFile(f);
              }}
              style={{
                border: '2px dashed var(--line)',
                borderRadius: 'var(--radius)',
                padding: '36px 18px',
                background: 'var(--bg)',
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer'
              }}
            >
              {uploading ? (
                <span style={{ fontWeight: 600 }}>Uploading…</span>
              ) : (
                <>
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    style={{ color: 'var(--ink)' }}
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div style={{ fontWeight: 600, marginTop: 10 }}>
                    Upload your ID photo
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--muted)',
                      marginTop: 4
                    }}
                  >
                    JPEG, PNG, HEIC or PDF · max 10MB
                  </div>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/heic,image/heif,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                  e.target.value = '';
                }}
              />
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-soft)',
                marginTop: 10,
                lineHeight: 1.5
              }}
            >
              ✓ Clear, in focus<br />
              ✓ All four corners visible<br />
              ✓ Good lighting, no glare<br />
              ✓ Address visible (if on the card)
            </div>
          </div>
        )}

        <div className="footer-cta pad-bottom">
          <button
            type="button"
            className="cta-full"
            onClick={() => navigate('/account')}
            style={
              status === 'pending' || status === 'approved'
                ? {}
                : { background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)' }
            }
          >
            <span>BACK TO MY ROTATION</span>
          </button>
        </div>
      </div>
    </section>
  );
}
